/**
 * @file ShipmentPrintActions.tsx
 * @description Действия по переданной отгрузке: статус и печать
 * (phase19.12 шаг 6).
 *
 * Кнопки печати рисуются по реестру `SHIPMENT_PRINT_FORMS`. СДЭК готовит
 * форму до ~20 секунд, поэтому на кнопке живёт спиннер с честной подписью
 * «Готовим PDF…», а повторное нажатие после ошибки разрешено.
 */

import { FileText, Loader2, RefreshCw } from "lucide-react";

import { Button } from "../ui/button";
import {
  SHIPMENT_PRINT_FORMS,
  type ShipmentPrintForm,
} from "../../lib/api-carriers-sdek";

interface Props {
  /** Форма, которая сейчас готовится у этой отгрузки. */
  printing: ShipmentPrintForm | null;
  acting: boolean;
  /** Трека ещё нет — СДЭК не закончил обработку заявки. */
  awaitingTrack?: boolean;
  onSync: () => void;
  onPrint: (form: ShipmentPrintForm) => void;
}

export function ShipmentPrintActions({
  printing,
  acting,
  awaitingTrack,
  onSync,
  onPrint,
}: Props) {
  return (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end">
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
        <Button
          size="sm"
          variant="outline"
          className="min-h-11"
          onClick={onSync}
          disabled={acting}
        >
          <RefreshCw className="mr-1 size-4" />
          Обновить статус
        </Button>

        {SHIPMENT_PRINT_FORMS.map((form) => {
          const busy = printing === form.key;
          return (
            <Button
              key={form.key}
              size="sm"
              variant="outline"
              className="min-h-11"
              onClick={() => onPrint(form.key)}
              disabled={busy}
              title="PDF формируется в СДЭК — это может занять до 20 секунд"
            >
              {busy ? (
                <Loader2 className="mr-1 size-4 animate-spin" />
              ) : (
                <FileText className="mr-1 size-4" />
              )}
              {busy ? "Готовим PDF…" : form.label}
            </Button>
          );
        })}
      </div>

      {awaitingTrack ? (
        <p className="text-xs text-on-surface-variant sm:text-right">
          Печать станет доступна, когда СДЭК присвоит трек-номер — обычно до
          20 секунд. Нажмите «Обновить статус».
        </p>
      ) : null}
    </div>
  );
}
